import { useCallback } from 'react';
import PropTypes from 'prop-types';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { useColorScheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { isNilOrEmpty } from 'ramda-extension';
import { useDispatch } from 'react-redux';

import { getMessage } from '@gef-ui/localization/messages';
import ArrowUpFromBracket from '@gef-ui/material-icons/ArrowUpFromBracket';

import AvatarHead from 'components/atoms/CustomSVGIcon/AvatarHead';
import { toggleSubordinateAssignStatusBasedOnAgencyAndManager } from 'features/views/settings/marketPlaceAgencies/editAgencyModal/actions';
import { joinString } from 'utils/string';

export const SubordinatesList = ({ assigned = [], unassigned = [], manager, agency }) => {
	const dispatch = useDispatch();
	const { mode: appTheme } = useColorScheme();

	const handleSubordinateAssignStatus = useCallback(
		(agencyId, managerLogin, subordinateLogin) => () => {
			dispatch(toggleSubordinateAssignStatusBasedOnAgencyAndManager(agencyId, managerLogin, subordinateLogin));
		},
		[dispatch],
	);

	return (
		<Stack spacing={1}>
			<Paper
				sx={{
					p: 3,
					borderRadius: 2,
					background: (theme) => (appTheme === 'light' ? theme.palette.background.neutral : 'background.300'),
				}}
			>
				<Stack spacing={1}>
					<Typography variant="body1">{getMessage('settings:editAgencyModal.assigned')}</Typography>
					<Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
						{!isNilOrEmpty(assigned) &&
							assigned.map((assignedSubordinate) => (
								<Chip
									icon={<AvatarHead />}
									key={assignedSubordinate?.userLogin}
									size="small"
									label={joinString(' ', [
										assignedSubordinate?.surname,
										assignedSubordinate.firstname,
									])}
									color={assignedSubordinate?.userLogin === manager ? 'warning' : 'primary'}
									onDelete={handleSubordinateAssignStatus(
										agency,
										manager,
										assignedSubordinate?.userLogin,
									)}
								/>
							))}
					</Stack>
				</Stack>
			</Paper>

			<Paper
				sx={{
					p: 3,
					borderRadius: 2,
					background: (theme) => (appTheme === 'light' ? theme.palette.background.neutral : 'background.300'),
				}}
			>
				<Stack spacing={1}>
					<Typography variant="body1">{getMessage('settings:editAgencyModal.unassigned')}</Typography>
					<Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
						{!isNilOrEmpty(unassigned) &&
							unassigned.map((unassignedSubordinate) => (
								<Chip
									icon={<AvatarHead />}
									key={unassignedSubordinate?.userLogin}
									size="small"
									label={joinString(' ', [
										unassignedSubordinate?.surname,
										unassignedSubordinate.firstname,
									])}
									color={unassignedSubordinate?.userLogin === manager ? 'warning' : 'primary'}
									onDelete={handleSubordinateAssignStatus(
										agency,
										manager,
										unassignedSubordinate?.userLogin,
									)}
									deleteIcon={<ArrowUpFromBracket />}
								/>
							))}
					</Stack>
				</Stack>
			</Paper>
		</Stack>
	);
};

SubordinatesList.propTypes = {
	agency: PropTypes.string,
	assigned: PropTypes.array,
	manager: PropTypes.object,
	unassigned: PropTypes.array,
};

export default SubordinatesList;
